// ProtectedRoute Component
// Route guard for authenticated users with email verification

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@features/auth';

import { LoadingSpinner } from '@/shared/ui';
import { PUBLIC_ROUTES } from '@/shared/constants';

interface ProtectedRouteProps {
    children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps): ReactNode => {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return <LoadingSpinner fullScreen text="Loading..." />;
    }

    if (!user) {
        console.warn('[ProtectedRoute] No user, redirecting from', location.pathname);
        return <Navigate to={PUBLIC_ROUTES.LOGIN} state={{ from: location }} replace />;
    }

    // Require email verification before accessing protected routes (skipped in emulator)
    if (!user.emailVerified && import.meta.env.VITE_USE_EMULATORS !== 'true') {
        process.env.NODE_ENV === 'development' && console.warn('[ProtectedRoute] Email not verified, redirecting to verify-email. User:', user.uid);
        return <Navigate to={PUBLIC_ROUTES.EMAIL_VERIFICATION} replace />;
    }

    if (!user.emailVerified && import.meta.env.VITE_USE_EMULATORS === 'true') {
        process.env.NODE_ENV === 'development' && console.log('[ProtectedRoute] Email not verified but running in emulator mode - allowing access');
    }

    return children;
};


export default ProtectedRoute;
